import { type CSSProperties } from 'react'

export type SelectOption = {
  value: string
  label: string
  disabled?: boolean
}

type SelectProps = {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  /** Optional heading shown at the top of the open menu. */
  title?: string
  /** Which edge of the trigger the menu aligns to. */
  align?: 'left' | 'right'
  disabled?: boolean
  ariaLabel?: string
  className?: string
  style?: CSSProperties
}

/** Shared single-select dropdown. Uses the native control for reliable keyboard,
 * scroll, and selected-label behavior across dashboard screens.
 */
export function Select({
  value,
  onChange,
  options,
  placeholder = 'Выберите…',
  title,
  align = 'left',
  disabled = false,
  ariaLabel,
  className = '',
  style,
}: SelectProps) {
  const hasSelectedOption = options.some((option) => option.value === value)

  return (
    <div
      className={`ds-select ds-select--native${className ? ` ${className}` : ''}`}
      style={style}
      data-align={align}
    >
      {title && <span className="sr-only">{title}</span>}
      <select
        className="ds-select__button ds-select__native"
        value={hasSelectedOption ? value : ''}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        aria-label={ariaLabel}
      >
        {!hasSelectedOption && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      <svg
        className="ds-select__native-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden="true"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
  )
}
